/**
 * Compliance Engine Service (AI)
 *
 * Wraps the Railway routes that ran as the `compliance-engine` Supabase
 * edge function in the prior architecture. The server-side run also persists
 * the result row to `compliance_engine_runs`, so this wrapper has no
 * separate `save` method — the prior `save()` was folded into `run()`.
 */

import { apiGet, apiPost, ApiError } from "@/lib/apiClient";

export interface ComplianceValidationSummary {
  total_items: number;
  passed: number;
  failed: number;
  flagged: number;
}

export interface ComplianceLineItemChecks {
  price_match: string;
  quantity_match: string;
  item_code_valid: string;
  uom_consistent: string;
  discount_applied: string;
  line_total_accurate: string;
  contract_item: string;
  ticket_item: string;
}

export interface ComplianceLineItem {
  invoice_item_id?: string;
  line_number: number;
  item_code: string;
  description: string;
  status: "PASS" | "FAIL" | "FLAG";
  notes: string;
  checks: ComplianceLineItemChecks;
  types: string | null;
  invoiced_amount: number | null;
  correct_amount: number | null;
  difference_per_unit: number | null;
  uom: string | null;
  quantity: number | null;
  total_impact: number | null;
}

export interface ComplianceMissingCharge {
  item_code: string;
  description: string;
  expected_reason: string;
  estimated_amount: number | null;
}

export interface ComplianceEngineResult {
  validation_summary: ComplianceValidationSummary;
  line_items: ComplianceLineItem[];
  missing_charges: ComplianceMissingCharge[];
  general_observations: string[];
}

export interface ComplianceEngineMetadata {
  model: string;
  processing_time_ms: number;
  price_book_pdf: boolean;
  price_book_pdf_message: string | null;
  field_ticket_pdf: boolean;
  field_ticket_pdf_message: string | null;
  price_book_brief: boolean;
  price_book_brief_message: string | null;
  system_prompt_source: string;
  system_prompt?: string;
  invoice_items_count: number;
  price_book_items_count: number;
  ticket_items_count: number;
}

export interface ComplianceEngineResponse {
  success: boolean;
  results?: ComplianceEngineResult;
  metadata?: ComplianceEngineMetadata;
  error?: string;
  /** Present when the server-side persist succeeded. */
  run_id?: string;
}

export interface ComplianceEngineRun {
  id: string;
  company_id: string;
  invoice_id: string;
  total_items: number | null;
  passed: number | null;
  failed: number | null;
  flagged: number | null;
  total_overcharge: number | null;
  total_undercharge: number | null;
  results: ComplianceEngineResult | null;
  metadata: ComplianceEngineMetadata | null;
  ai_model: string | null;
  processing_time_ms: number | null;
  created: string;
  created_by: string | null;
}

export interface EngineDashboardRow {
  invoice_id: string;
  invoice_number: string;
  supplier_name: string;
  failed_count: number;
  total_overcharge: number;
}

export const complianceEngineService = {
  /**
   * Run the AI compliance engine for an invoice. The server persists the run
   * row as part of the same call — no separate save step required.
   */
  async run(invoiceId: string): Promise<ComplianceEngineResponse> {
    try {
      return await apiPost<ComplianceEngineResponse>("/api/compliance/engine/run", {
        invoice_id: invoiceId,
      });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err);
      return { success: false, error: message };
    }
  },

  /**
   * Fetch the most recent compliance engine run for an invoice.
   */
  async latestByInvoice(invoiceId: string): Promise<ComplianceEngineRun | null> {
    try {
      const result = await apiGet<{ data: ComplianceEngineRun | null }>(
        `/api/compliance/engine/by-invoice/${encodeURIComponent(invoiceId)}/latest`,
      );
      return result.data;
    } catch (err) {
      console.error("Failed to fetch latest compliance run:", err);
      return null;
    }
  },

  /**
   * Recent AI compliance engine runs with failures, deduplicated by invoice
   * (latest run wins, capped at 20). The companyId param is accepted for
   * backward-compat with the prior service signature; the server derives
   * company scope from the JWT.
   */
  async getEngineDashboardSummary(
    _companyId: string,
    sinceDate?: string,
  ): Promise<{ data: EngineDashboardRow[]; error: Error | null }> {
    try {
      const qs = sinceDate ? `?sinceDate=${encodeURIComponent(sinceDate)}` : "";
      const result = await apiGet<{ data: EngineDashboardRow[] }>(
        `/api/compliance/engine/dashboard${qs}`,
      );
      return { data: result.data, error: null };
    } catch (err) {
      const error = err instanceof ApiError ? err : err instanceof Error ? err : new Error(String(err));
      return { data: [], error };
    }
  },
};
